import { useState, useEffect, useRef } from "react";

const GOLD = "#C9A84C";
const GOLD_LIGHT = "#E8C97A";
const BG = "#0A0A0A";
const BG2 = "#111111";
const BG3 = "#1A1A1A";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>
      {children}
    </div>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const links = ["About", "Services", "Content", "Contact"];
  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? "rgba(10,10,10,0.97)" : "transparent",
      borderBottom: scrolled ? `1px solid ${GOLD}22` : "none",
      transition: "background 0.4s, border 0.4s",
      padding: "0 24px"
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22, color: GOLD }}>✚</span>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: 1 }}>
            The Post <span style={{ color: GOLD }}>Doctor</span>
          </span>
        </div>
        {/* Desktop */}
        <div style={{ display: "flex", gap: 32, alignItems: "center" }} className="desktop-nav">
          {links.map(l => (
            <a key={l} href={`#${l.toLowerCase()}`} style={{ color: "#ccc", textDecoration: "none", fontSize: 14, letterSpacing: 1, textTransform: "uppercase", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = GOLD}
              onMouseLeave={e => e.target.style.color = "#ccc"}>
              {l}
            </a>
          ))}
          <a href="https://meetings-na2.hubspot.com/jeff-sengpiehl" target="_blank" rel="noopener noreferrer" style={{
            background: GOLD, color: "#000", padding: "8px 20px", borderRadius: 4,
            fontWeight: 700, fontSize: 13, textDecoration: "none", letterSpacing: 1,
            transition: "background 0.2s"
          }}
            onMouseEnter={e => e.target.style.background = GOLD_LIGHT}
            onMouseLeave={e => e.target.style.background = GOLD}>
            BOOK A CALL
          </a>
        </div>
        {/* Mobile burger */}
        <button onClick={() => setOpen(!open)} style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 8 }} className="burger">
          <div style={{ width: 24, height: 2, background: GOLD, marginBottom: 5, transition: "0.3s" }} />
          <div style={{ width: 24, height: 2, background: GOLD, marginBottom: 5 }} />
          <div style={{ width: 24, height: 2, background: GOLD }} />
        </button>
      </div>
      {open && (
        <div style={{ background: "#0f0f0f", borderTop: `1px solid ${GOLD}33`, padding: "16px 24px 24px" }}>
          {[...links, "Book a Call"].map(l => (
            <a key={l} href={`#${l === "Book a Call" ? "contact" : l.toLowerCase()}`}
              onClick={() => setOpen(false)}
              style={{ display: "block", color: l === "Book a Call" ? GOLD : "#ccc", padding: "12px 0", textDecoration: "none", fontSize: 15, borderBottom: `1px solid #222`, letterSpacing: 1 }}>
              {l.toUpperCase()}
            </a>
          ))}
        </div>
      )}
      <style>{`
        @media (max-width: 700px) {
          .desktop-nav { display: none !important; }
          .burger { display: block !important; }
        }
      `}</style>
    </nav>
  );
}

function Hero() {
  return (
    <section style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: `radial-gradient(ellipse at 60% 40%, #1a1400 0%, ${BG} 60%)`,
      padding: "120px 24px 80px", textAlign: "center", position: "relative", overflow: "hidden"
    }}>
      {/* Decorative ring */}
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        border: `1px solid ${GOLD}18`, top: "50%", left: "50%",
        transform: "translate(-50%,-50%)", pointerEvents: "none"
      }} />
      <div style={{
        position: "absolute", width: 900, height: 900, borderRadius: "50%",
        border: `1px solid ${GOLD}0A`, top: "50%", left: "50%",
        transform: "translate(-50%,-50%)", pointerEvents: "none"
      }} />
      <div style={{ maxWidth: 820, position: "relative" }}>
        <div style={{ color: GOLD, fontSize: 13, letterSpacing: 4, textTransform: "uppercase", marginBottom: 20, fontWeight: 600 }}>
          ✚ &nbsp; Vendor-Neutral Technology Advisory
        </div>
        <h1 style={{ fontSize: "clamp(38px, 7vw, 72px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, margin: "0 0 24px", letterSpacing: -1 }}>
          The Post&nbsp;<span style={{ color: GOLD }}>Doctor</span>
        </h1>
        <p style={{ fontSize: "clamp(16px, 2.5vw, 22px)", color: "#aaa", lineHeight: 1.7, maxWidth: 640, margin: "0 auto 16px" }}>
          Independent technology advisory for post-production &amp; broadcast.
          30+ years of engineering leadership — no vendor agenda, just the right prescription.
        </p>
        <p style={{ fontSize: 15, color: "#666", marginBottom: 24 }}>
          Advisor · Board Member · Fractional CTO · Systems Consulting · Storage Architecture
        </p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginBottom: 44 }}>
          {[
            { icon: "🎤", label: "Add Jeff to your Technical Panel" },
            { icon: "🎙️", label: "Have The Doctor on your Podcast" },
          ].map(({ icon, label }) => (
            <a key={label} href="#contact" style={{
              display: "flex", alignItems: "center", gap: 10,
              background: `${GOLD}12`, border: `1px solid ${GOLD}44`,
              color: GOLD, padding: "10px 20px", borderRadius: 6,
              fontSize: 13, fontWeight: 600, textDecoration: "none", letterSpacing: 0.5,
              transition: "all 0.2s"
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `${GOLD}22`; e.currentTarget.style.borderColor = GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${GOLD}12`; e.currentTarget.style.borderColor = `${GOLD}44`; }}>
              <span>{icon}</span> {label}
            </a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="https://meetings-na2.hubspot.com/jeff-sengpiehl" target="_blank" rel="noopener noreferrer" style={{
            background: GOLD, color: "#000", padding: "14px 36px", borderRadius: 4,
            fontWeight: 800, fontSize: 15, textDecoration: "none", letterSpacing: 1,
            boxShadow: `0 0 32px ${GOLD}44`, transition: "all 0.2s"
          }}
            onMouseEnter={e => { e.target.style.background = GOLD_LIGHT; e.target.style.boxShadow = `0 0 48px ${GOLD}66`; }}
            onMouseLeave={e => { e.target.style.background = GOLD; e.target.style.boxShadow = `0 0 32px ${GOLD}44`; }}>
            BOOK A CONSULTATION
          </a>
          <a href="#about" style={{
            background: "transparent", color: GOLD, padding: "14px 36px", borderRadius: 4,
            fontWeight: 700, fontSize: 15, textDecoration: "none", letterSpacing: 1,
            border: `1px solid ${GOLD}66`, transition: "all 0.2s"
          }}
            onMouseEnter={e => { e.target.style.borderColor = GOLD; e.target.style.background = `${GOLD}11`; }}
            onMouseLeave={e => { e.target.style.borderColor = `${GOLD}66`; e.target.style.background = "transparent"; }}>
            LEARN MORE
          </a>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" style={{ background: BG2, padding: "100px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
        <FadeIn>
          <div style={{ color: GOLD, fontSize: 12, letterSpacing: 4, textTransform: "uppercase", marginBottom: 16 }}>About</div>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", color: "#fff", fontWeight: 800, margin: "0 0 24px", lineHeight: 1.2 }}>
            Diagnosing Complex<br /><span style={{ color: GOLD }}>Technology Challenges</span>
          </h2>
          <p style={{ color: "#999", lineHeight: 1.8, fontSize: 16, marginBottom: 20 }}>
            I'm Jeff Sengpiehl — a media and entertainment technology executive with over 30 years spanning broadcast engineering, post-production infrastructure, and C-suite technology leadership.
          </p>
          <p style={{ color: "#999", lineHeight: 1.8, fontSize: 16, marginBottom: 20 }}>
            My career includes CTO and VP-level roles — with Media and Storage Manufacturers, System Integrators, Post Production houses, and Broadcast Networks. Experience — from on-prem hardware to tape libraries to cloud architectures — gives me a cross-sector perspective that brings you the best recommendations for facilities, tools, and workflows that others don't have the breadth to match.
          </p>
          <p style={{ color: "#999", lineHeight: 1.8, fontSize: 16 }}>
            The Post Doctor is here because this industry needs a trusted, non-biased voice. I don't sell equipment. I don't push platforms. I prescribe what's right for your operation.
          </p>
        </FadeIn>
        <FadeIn delay={0.2}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { num: "30+", label: "Years of Industry Experience" },
              { num: "C-Suite", label: "Executive Leadership Background" },
              { num: "100%", label: "Vendor Neutral, Always" },
              { num: "M&E", label: "Post-Production & Broadcast Focus" },
            ].map(({ num, label }) => (
              <div key={label} style={{
                background: BG3, border: `1px solid ${GOLD}22`, borderRadius: 8,
                padding: "28px 20px", textAlign: "center"
              }}>
                <div style={{ color: GOLD, fontSize: 28, fontWeight: 800, marginBottom: 8 }}>{num}</div>
                <div style={{ color: "#888", fontSize: 13, lineHeight: 1.5 }}>{label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
      <style>{`@media(max-width:700px){#about > div{grid-template-columns:1fr !important; gap:40px !important;}}`}</style>
    </section>
  );
}

function Services() {
  const services = [
    {
      icon: "🏥",
      title: "Fractional CTO",
      desc: "Executive technology leadership without the full-time overhead. Strategic guidance, team mentoring, vendor negotiations, and roadmap development — on your timeline."
    },
    {
      icon: "🔬",
      title: "Technology Advisory",
      desc: "Independent, vendor-neutral assessments of your infrastructure, workflows, and architecture. Get an honest diagnosis before you write the check."
    },
    {
      icon: "💾",
      title: "Storage Architecture",
      desc: "Deep expertise in LTO tape, NAS/SAN, cloud tiers (S3, Glacier), and hybrid strategies. TCO modeling, migration planning, and long-term archive design."
    },
    {
      icon: "📡",
      title: "Broadcast & Post Consulting",
      desc: "End-to-end workflow consulting for broadcast operations and post-production facilities — MAM selection, pipeline design, compliance, and TPN readiness."
    },
    {
      icon: "🧭",
      title: "CTO Search Advisory",
      desc: "Helping organizations find, evaluate, and onboard technology leadership. I know what good looks like — and what red flags look like."
    },
    {
      icon: "📋",
      title: "Executive Interim CTO",
      desc: "Step-in technology leadership during transitions, M&A due diligence, or critical infrastructure projects. Steady hands when stakes are highest."
    },
    {
      icon: "🖥️",
      title: "Systems Design Review",
      desc: "Independent review of your proposed or existing system architecture. Identify gaps, validate decisions, and get a second opinion before you commit budget."
    }
  ];
  return (
    <section id="services" style={{ background: BG, padding: "100px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ color: GOLD, fontSize: 12, letterSpacing: 4, textTransform: "uppercase", marginBottom: 16 }}>Services</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", color: "#fff", fontWeight: 800, margin: "0 0 16px" }}>
              The <span style={{ color: GOLD }}>Prescription</span>
            </h2>
            <p style={{ color: "#888", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>
              Tailored engagements for organizations navigating technology complexity in media & entertainment.
            </p>
          </div>
        </FadeIn>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
          {services.map(({ icon, title, desc }, i) => (
            <FadeIn key={title} delay={i * 0.08}>
              <div style={{
                background: BG3, border: `1px solid ${GOLD}1A`, borderRadius: 10,
                padding: "32px 28px", height: "100%", boxSizing: "border-box",
                transition: "border-color 0.3s, transform 0.3s",
                cursor: "default"
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${GOLD}66`; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = `${GOLD}1A`; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ fontSize: 32, marginBottom: 16 }}>{icon}</div>
                <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>{title}</h3>
                <p style={{ color: "#888", fontSize: 14, lineHeight: 1.7, margin: 0 }}>{desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function Content() {
  const series = ["The Script", "Storage Specialist Referral"];
  const channels = [
    {
      icon: "▶",
      name: "YouTube",
      handle: "@ThePostDoctor",
      desc: "Long-form deep dives, technology breakdowns, and industry commentary.",
      href: "https://youtube.com/@ThePostDoctor",
      color: "#FF4444"
    },
    {
      icon: "✉",
      name: "Substack",
      handle: "The Post Doctor",
      desc: "In-depth articles, industry analysis, and The Prescription newsletter.",
      href: "https://thepostdoctor.substack.com/",
      color: GOLD
    },
    {
      icon: "in",
      name: "LinkedIn",
      handle: "Jeff Sengpiehl",
      desc: "Industry insights, career updates, and professional commentary.",
      href: "https://linkedin.com/in/jeffsengpiehl",
      color: "#0A66C2"
    },
    {
      icon: "𝕏",
      name: "X / Twitter",
      handle: "@DocAvid",
      desc: "Real-time takes on post-production and broadcast technology.",
      href: "https://x.com/DocAvid",
      color: "#fff"
    }
  ];
  return (
    <section id="content" style={{ background: BG2, padding: "100px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ color: GOLD, fontSize: 12, letterSpacing: 4, textTransform: "uppercase", marginBottom: 16 }}>Content Hub</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", color: "#fff", fontWeight: 800, margin: "0 0 16px" }}>
              The Doctor Is <span style={{ color: GOLD }}>In</span>
            </h2>
            <p style={{ color: "#888", fontSize: 16, maxWidth: 540, margin: "0 auto 40px" }}>
              Follow along across platforms for vendor-neutral education, industry analysis, and real talk from 30+ years in the trenches.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 8 }}>
              {series.map(s => (
                <span key={s} style={{
                  background: `${GOLD}15`, border: `1px solid ${GOLD}33`,
                  color: GOLD, fontSize: 12, padding: "6px 14px", borderRadius: 20, letterSpacing: 0.5
                }}>{s}</span>
              ))}
            </div>
            <p style={{ color: "#555", fontSize: 12, letterSpacing: 2, textTransform: "uppercase" }}>Recurring Series</p>
          </div>
        </FadeIn>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
          {channels.map(({ icon, name, handle, desc, href, color }, i) => (
            <FadeIn key={name} delay={i * 0.1}>
              <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <div style={{
                  background: BG3, border: `1px solid #222`, borderRadius: 10,
                  padding: "28px 24px", transition: "border-color 0.3s, transform 0.3s"
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = "translateY(-4px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#222"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ fontSize: 28, color, marginBottom: 12, fontWeight: 900 }}>{icon}</div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{name}</div>
                  <div style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>{handle}</div>
                  <p style={{ color: "#888", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{desc}</p>
                </div>
              </a>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" style={{ background: BG, padding: "100px 24px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div style={{ color: GOLD, fontSize: 12, letterSpacing: 4, textTransform: "uppercase", marginBottom: 16 }}>Contact</div>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", color: "#fff", fontWeight: 800, margin: "0 0 16px" }}>
              Book a <span style={{ color: GOLD }}>Consultation</span>
            </h2>
            <p style={{ color: "#888", fontSize: 16 }}>
              Ready for a vendor-neutral perspective on your technology challenges? Let's talk.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div style={{ background: BG3, border: `1px solid ${GOLD}22`, borderRadius: 12, overflow: "hidden" }}>
            <iframe
              src="https://245313830.hs-sites-na2.com/en/book-an-initial-discussion"
              width="100%"
              height="800"
              frameBorder="0"
              title="Book a Consultation"
              style={{ display: "block" }}
            />
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ background: "#070707", borderTop: `1px solid ${GOLD}18`, padding: "40px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20, color: GOLD }}>✚</span>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
            The Post <span style={{ color: GOLD }}>Doctor</span>
          </span>
        </div>
        <p style={{ color: "#555", fontSize: 13, margin: 0 }}>
          Vendor-neutral technology advisory for post-production &amp; broadcast.
        </p>
        <p style={{ color: "#555", fontSize: 12, margin: 0 }}>
          © {new Date().getFullYear()} The Post Doctor · Jeff Sengpiehl
        </p>
        <a href="mailto:Contact@ThePostDr.Com" style={{ color: GOLD, fontSize: 13, textDecoration: "none" }}>Contact@ThePostDr.Com</a>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div style={{ background: BG, minHeight: "100vh", fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", color: "#fff" }}>
      <Nav />
      <Hero />
      <About />
      <Services />
      <Content />
      <Contact />
      <Footer />
    </div>
  );
}